import { Component, OnInit, ViewChild } from '@angular/core';
import { publicMethods, baseUrl } from '../../globals';
import { BasicCompanyMatcherService } from '../../services/basic-company-matcher.service';
import { CompanyMatcherComponent } from '../company-matcher/company-matcher.component';

@Component({
    selector: 'upload-companies-v2',
    templateUrl: './upload-companies-v2.component.html',
    styleUrls: ['./upload-companies-v2.component.css'],
    standalone: false
})
export class UploadCompaniesV2Component implements OnInit {
  stepNumber = 0;
  isCrmMatcherRoute = false;
  showCrmWizardEmptyState = false;
  crmEmptyStateMessage =
    'No CRM matcher process was found. Please start matcher from the';

  additionalHeaders: any;
  customColumns: any;
  allocatedCustomColumnIds: Array<any> = [];
  shouldOverwrite: boolean = false;

  isLoading: any;

  rowsProcessed: any;
  totalNumberOfRows: any;
  userHasAccess = false;
  status: any;

  failedRows: Array<any>;
  textUploadContent = '';
  useAiWrangle = false;
  private pendingAutoAdvance = false;

  // Required for file upload
  self = this;
  // Required for timeout caller
  continuousCaller;

  @ViewChild('childCompanyMatcher')
  companyMatcherComponent: CompanyMatcherComponent;

  constructor(
    private basicCompanyMatcherService: BasicCompanyMatcherService,
    private pubMethods: publicMethods
  ) {}

  ngOnInit(): void {
    document.title = 'Matcher';
    this.isCrmMatcherRoute = this.getCurrentPathname().startsWith('/matcher/crm');
    this.isLoading = this.basicCompanyMatcherService.isLoading;
    this.loadPage();
  }

  private getCurrentPathname(): string {
    if (typeof window === 'undefined' || !window.location) {
      return '';
    }
    return window.location.pathname || '';
  }

  loadPage(): void {
    this.basicCompanyMatcherService
      .loadStatus()
      .subscribe(data => this.updatePageBasedUponStatus(data));
  }

  updatePageBasedUponStatus(data): void {
    const status =
      data && typeof data === 'object' && !Array.isArray(data)
        ? data['status']
        : null;

    if (
      this.isCrmMatcherRoute &&
      status !== 'matching' &&
      status !== 'completed_matching'
    ) {
      this.status = status;
      this.showCrmWizardEmptyState = true;
      this.crmEmptyStateMessage =
        status === 'defining_settings'
          ? 'CRM matcher settings are being defined. Please start matcher from the'
          : 'No CRM matcher process was found. Please start matcher from the';
      return;
    }

    this.showCrmWizardEmptyState = false;
    this.totalNumberOfRows = data?.total_rows;
    this.userHasAccess = true;
    this.status = status;
    if (this.status === 'defining_settings') {
      this.loadSettings();
    }
    if (this.status === 'matching') {
      this.testCompanyMatcherProgress();
    }
    if (this.status === 'completed_matching') {
      this.initMatcher();
    }
  }

  loadSettings(): void {
    this.basicCompanyMatcherService
      .loadMatcherSettings()
      .subscribe(data => this.updateConfigurationSettings(data));
  }

  updateConfigurationSettings(data): void {
    this.customColumns = (data && data.custom_columns) || [];
    this.additionalHeaders = (data && data.additional_columns) || [];
    this.allocatedCustomColumnIds = [];

    const additionalColumnCount = this.additionalHeaders.length || 0;
    const hasExistingMappings = data && data.mappings !== null;

    if (additionalColumnCount === 0) {
      this.stepNumber = hasExistingMappings || this.pendingAutoAdvance ? 2 : 0;
      this.pendingAutoAdvance = false;
      return;
    }

    this.pendingAutoAdvance = false;
    this.stepNumber = hasExistingMappings ? 2 : 1;
  }

  awaitResultsFromMatchingProcess(data): void {
    this.rowsProcessed = data.rows_processed;
    this.totalNumberOfRows = data.total_rows;
    this.status = data.status;
    if (this.status === 'matching') {
      this.continuousCaller = setTimeout(() => {
        this.testCompanyMatcherProgress();
      }, 10000);
    }
    if (this.status === 'completed_matching') {
      this.initMatcher();
    }
  }

  testCompanyMatcherProgress() {
    this.basicCompanyMatcherService
      .loadMatcherProgress()
      .subscribe(data => this.awaitResultsFromMatchingProcess(data));
  }

  initMatcher(): void {
    var self = this;
    setTimeout(function () {
      self.companyMatcherComponent.loadDataForUser();
    }, 1);
  }

  // Step 0

  downloadSampleCSVFile(): void {
    window.open(baseUrl + '/download_company_matcher_sample_csv/');
  }

  fileUploadComplete(response, _self) {
    _self.updateConfigAndGoToNextStep(response);
  }

  submitTextUpload(): void {
    const trimmedText = this.textUploadContent.trim();
    if (!trimmedText) {
      return;
    }
    this.basicCompanyMatcherService
      .uploadMatcherText(this.textUploadContent, this.useAiWrangle)
      .subscribe(response => {
        if (!this.isValidUploadPayload(response)) {
          return;
        }
        this.fileUploadComplete(response, this);
        this.textUploadContent = '';
      });
  }

  updateConfigAndGoToNextStep(data): void {
    if (!this.isValidUploadPayload(data)) {
      return;
    }

    this.customColumns = data.custom_columns || [];
    this.additionalHeaders = data.additional_columns || [];
    this.allocatedCustomColumnIds = [];

    const additionalColumnCount = this.additionalHeaders.length || 0;

    if (additionalColumnCount === 0) {
      this.pendingAutoAdvance = true;
      this.shouldOverwrite = false;
      this.stepNumber = 2;
      this.submitMatcherMappings([]);
      return;
    }

    this.pendingAutoAdvance = false;
    this.stepNumber = 1;
  }

  // Step 0

  // Step 1
  updateAllocatedCustomColumnIds() {
    const headers = Array.isArray(this.additionalHeaders)
      ? this.additionalHeaders
      : [];
    this.allocatedCustomColumnIds = headers
      .filter(
        el =>
          el.selectedCustomColumn != undefined &&
          el.selectedCustomColumn != 'ignore_column'
      )
      .map(function (el) {
        return el.selectedCustomColumn.id;
      });
  }

  submitMatcherMappings(headers: any = this.additionalHeaders) {
    const submission = headers || [];
    const isEmptySubmission = Array.isArray(submission)
      ? submission.length === 0
      : false;

    this.basicCompanyMatcherService
      .submitMatcherMappings(submission, this.shouldOverwrite)
      .subscribe(data =>
        this.handleMappingsSubmissionResponse(data, isEmptySubmission)
      );
  }
  // Step 1

  // Step 2
  startMatchingProcess() {
    if (this.isCrmMatcherRoute) {
      this.pubMethods.showInfoMessage(
        'Please start matcher from the CRM wizard.'
      );
      return;
    }
    this.basicCompanyMatcherService
      .startMatchingProcess()
      .subscribe(data => this.updatePageBasedUponStatus(data));
  }
  // Step 2

  cancelUserMatchingProcess() {
    this.allocatedCustomColumnIds = [];
    this.failedRows = [];
    this.stepNumber = 0;
    this.pendingAutoAdvance = false;
    this.basicCompanyMatcherService
      .cancelMatchingProcess()
      .subscribe(data => this.updatePageBasedUponStatus(data));
  }

  showNumberUpdated(data): void {
    if (data['failed_rows']) {
      this.failedRows = data.failed_rows;
    }
    if (data['created']) {
      this.pubMethods.showInfoMessage(
        'Processed ' + data['created'] + ' rows.'
      );
    }
  }

  createMatcherRecordsForSelectedOnPage(rowIdsToSelectedCompanyIds): void {
    this.basicCompanyMatcherService
      .createMatcherRecordsForSelectedOnPage(rowIdsToSelectedCompanyIds)
      .subscribe(data => this.showNumberUpdated(data));
  }

  createMatcherRecordsForAll(params): void {
    let lowerMatchLevel = params[0];
    let upperMatchLevel = params[1];
    this.basicCompanyMatcherService
      .createMatcherRecordsForAll(lowerMatchLevel, upperMatchLevel)
      .subscribe(data => this.showNumberUpdated(data));
  }

  createMatcherTagsForSelectedOnPage(event): void {
    if (!event || !event.tagId) {
      return;
    }
    this.basicCompanyMatcherService
      .createMatcherTagsForSelectedOnPage(
        event.tagId,
        event.rowIdsToSelectedCompanyId || {}
      )
      .subscribe(data =>
        this.handleMatcherTagResponse(
          data,
          'the selected matches on this page'
        )
      );
  }

  createMatcherTagsForAll(event): void {
    if (!event || !event.tagId) {
      return;
    }
    this.basicCompanyMatcherService
      .createMatcherTagsForAll(
        event.tagId,
        event.lowerMatchLevel,
        event.upperMatchLevel
      )
      .subscribe(data =>
        this.handleMatcherTagResponse(
          data,
          'the top matches across all rows'
        )
      );
  }

  handleMatcherTagResponse(data, scopeDescription: string): void {
    if (data && data.failed_rows) {
      this.failedRows = data.failed_rows;
    }
    if (data) {
      const taggedCount =
        data.tagged !== undefined ? data.tagged : data.total_tagged;
      if (taggedCount !== undefined) {
        const noun = taggedCount === 1 ? 'company' : 'companies';
        this.pubMethods.showInfoMessage(
          `Tagged ${taggedCount} ${noun} for ${scopeDescription}.`
        );
        return;
      }
    }
    this.pubMethods.showInfoMessage(
      `Submitted a tagging request for ${scopeDescription}.`
    );
  }

  handleMappingsSubmissionResponse(
    data: any,
    isEmptySubmission: boolean
  ): void {
    this.pendingAutoAdvance = false;

    if (
      data &&
      typeof data === 'object' &&
      !Array.isArray(data) &&
      Object.prototype.hasOwnProperty.call(data, 'status')
    ) {
      this.updatePageBasedUponStatus(data);
      return;
    }

    if (isEmptySubmission) {
      this.stepNumber = 0;
    }
  }

  isValidUploadPayload(data: any): boolean {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
      return false;
    }

    return (
      Object.prototype.hasOwnProperty.call(data, 'custom_columns') &&
      Object.prototype.hasOwnProperty.call(data, 'additional_columns')
    );
  }
}
